import { UserOutlined, WarningOutlined } from '@ant-design/icons'
import { Button, Col, Input, Row } from 'antd'
import { useState } from 'react'
import Hotkeys from 'react-hot-keys'
import styled from 'styled-components'
import { tabFields } from '~app/configuration/labels'
import type {
  DocumentFragment,
  DocumentStage,
} from '~app/graph/generated/graphql'
import type { AutoFocusFieldResult } from '~app/hooks/useField'
import i18n from '~app/i18n/i18n'
import { RemoveCustomerDocumentButton } from '../RemoveCustomerDocumentButton'
import CustomerInfoTable from './CustomerInfoTable'
import PublicIdInput from './PublicIdInput'
import RuleWarnings from './RuleWarnings'
import { useReportNatifDocumentExtractionErrors } from './useReportNatifDocumentExtractionErrors'
import { useReportNatifTagExtractionErrors } from './useReportNatifTagExtractionErrors'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const styles: InlineStyles = {
  dateTextStyle: {
    fontSize: 14,
    margin: '10px 5px',
  },
  flexColumnStyle: {
    flexGrow: 1,
    display: 'flex',
    justifyContent: 'flex-end',
    paddingRight: '32px',
  },
}

interface Props {
  documentId: string
  documentCreatedAt: string
  documentRules: DocumentFragment['rules'] | null
  documentStage: DocumentStage
  documentTags: string[]
  customerFirstName?: string
  customerLastName?: string
  customerPublicId?: string
  customerCompanyName?: string
  subjectField: AutoFocusFieldResult<string>
  onRemoveDocumentDone?: () => void
}

const PublicIdCol = styled(Col)`
  display: flex;
  align-items: center;
  p {
    margin: 0 10px;
  }
  input {
    max-width: 100px;
  }
  .anticon {
    margin-left: 10px;
    cursor: pointer;
  }
`

const Title = ({
  children,
  documentCreatedAt,
  documentId,
  documentRules,
  documentStage,
  documentTags,
  customerFirstName,
  customerLastName,
  customerPublicId,
  subjectField,
  customerCompanyName,
  onRemoveDocumentDone,
}: React.PropsWithChildren<Props>) => {
  const [showCustomerInfoTable, setShowCustomerInfoTable] = useState(false)
  const {
    reportNatifDocumentExtractionErrors,
    loading: loadingDocumentReport,
  } = useReportNatifDocumentExtractionErrors({
    documentId,
  })
  const { reportNatifTagExtractionErrors, loading: loadingTagReport } =
    useReportNatifTagExtractionErrors({
      documentId,
    })

  const reportTagsErrors = () => {
    reportNatifTagExtractionErrors(documentTags)
  }

  return (
    <Wrapper>
      <Row align="middle" justify="start">
        <Col span={3}>
          <p style={styles.dateTextStyle}>{documentCreatedAt}</p>
        </Col>
        <Col>
          <p style={styles.dateTextStyle}>
            {customerCompanyName ? `${customerCompanyName} / ` : ''}
            {customerFirstName} {customerLastName}
          </p>
        </Col>
        <PublicIdCol span={2}>
          <PublicIdInput
            customerPublicId={customerPublicId || ''}
            documentId={documentId}
          />
        </PublicIdCol>
        {documentRules && (
          <Col>
            <RuleWarnings rules={documentRules} />
          </Col>
        )}
        <Col style={styles.flexColumnStyle}>
          <Hotkeys
            keyName="shift+s"
            onKeyDown={() => setShowCustomerInfoTable(!showCustomerInfoTable)}
          >
            <Button
              icon={<UserOutlined />}
              style={{ marginRight: 10 }}
              size="small"
              disabled={!customerPublicId}
              type={showCustomerInfoTable ? 'primary' : 'default'}
              onClick={() => setShowCustomerInfoTable(!showCustomerInfoTable)}
            >
              {i18n.t('titles.customerDetails')}
            </Button>
          </Hotkeys>
          <Hotkeys
            keyName="shift+r+d"
            onKeyDown={reportNatifDocumentExtractionErrors}
          >
            <Button
              icon={<WarningOutlined />}
              style={{ marginRight: 10 }}
              size="small"
              danger
              onClick={reportNatifDocumentExtractionErrors}
              loading={loadingDocumentReport}
              disabled={loadingDocumentReport}
              ghost
            >
              {i18n.t('natifErrorReport.document.buttonTitle')}
            </Button>
          </Hotkeys>
          <Hotkeys keyName="shift+r+t" onKeyDown={reportTagsErrors}>
            <Button
              icon={<WarningOutlined />}
              style={{ marginRight: 10 }}
              size="small"
              danger
              onClick={reportTagsErrors}
              loading={loadingTagReport}
              disabled={loadingTagReport}
              ghost
            >
              {i18n.t('natifErrorReport.tags.buttonTitle')}
            </Button>
          </Hotkeys>
          <RemoveCustomerDocumentButton
            documentId={documentId}
            documentStage={documentStage}
            onDone={onRemoveDocumentDone}
          />
        </Col>
      </Row>
      <Row>
        <Input
          {...subjectField.inputField}
          placeholder={i18n.t('documentView.unknown')}
          tabIndex={tabFields.subject}
        />
      </Row>
      {showCustomerInfoTable && (
        <Row>
          <CustomerInfoTable customerPublicId={customerPublicId} />
        </Row>
      )}
      {children}
    </Wrapper>
  )
}

export default Title
